import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import PageSkeleton, { ListSkeleton } from '../components/Skeleton';

const TEAL = '#1A6B54';
const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const RED = '#C0392B';

const STATUS_MAP = {
  pending_payment: { label: '待付款', color: '#E67E22', step: 0 },
  paid:            { label: '已付款', color: TEAL, step: 1 },
  shipping:        { label: '已发货', color: '#3498DB', step: 2 },
  delivered:       { label: '已送达', color: '#2ECC71', step: 3 },
  completed:       { label: '已完成', color: TEAL, step: 4 },
  cancelled:       { label: '已取消', color: '#999', step: -1 },
  refunded:        { label: '已退款', color: '#999', step: -1 },
};

const STEPS = [
  { key: 'pending_payment', label: '下单' },
  { key: 'paid', label: '付款' },
  { key: 'shipping', label: '发货' },
  { key: 'delivered', label: '收货' },
  { key: 'completed', label: '完成' },
];

export default function MarketOrderDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchOrder = async () => {
    try {
      const res = await api.get(`/market/orders/${id}`);
      setOrder(res.data);
    } catch {
      message.error('订单不存在');
      navigate('/market');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchOrder(); }, [id]);

  if (loading) return <ListSkeleton rows={4} />;
  if (!order) return null;

  const status = STATUS_MAP[order.status] || { label: order.status, color: '#999', step: -1 };
  const currentStep = status.step;
  const priceCny = Number(order.price_cny);
  const photos = order.photos?.length > 0
    ? (typeof order.photos === 'string' ? JSON.parse(order.photos) : order.photos)
    : [];
  const photo = photos[0] || `https://placehold.co/100x100/F5EDD8/B8892A?text=?`;

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      if (action === 'cancel') {
        await api.patch(`/market/orders/${id}`, { status: 'cancelled' });
        message.success('订单已取消');
      } else if (action === 'confirm') {
        await api.patch(`/market/orders/${id}`, { status: 'completed' });
        message.success('已确认收货');
      } else if (action === 'pay') {
        await api.post(`/market/orders/${id}/pay`);
        message.success('付款成功');
      } else if (action === 'refund') {
        await api.post(`/refunds/market/${id}`, { reason: refundReason });
        message.success('退款申请已提交');
        setRefundModalOpen(false);
        setRefundReason('');
      }
      setConfirmModalOpen(false);
      fetchOrder();
    } catch (e) {
      message.error(e.response?.data?.error || '操作失败');
    } finally { setActionLoading(false); }
  };

  const px = isMobile ? 16 : 48;

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: isMobile ? 140 : 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto', padding: `0 ${isMobile ? 0 : 24}px` }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK,
            padding: '0 8px 0 0',
          }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>订单详情</span>
          <div style={{ width: 24 }} />
        </div>

        {/* ── Status Banner ── */}
        <div style={{
          background: currentStep >= 0
            ? 'linear-gradient(135deg, #1A2E28 0%, #1F3A32 100%)'
            : '#F5F5F5',
          padding: '20px 16px', margin: `0 ${isMobile ? 0 : px}px`,
          borderRadius: isMobile ? 0 : '10px 10px 0 0',
          marginTop: isMobile ? 0 : 16,
        }}>
          <div style={{
            fontSize: 20, fontWeight: 700,
            color: currentStep >= 0 ? '#fff' : '#999',
            marginBottom: 4,
          }}>
            {status.label}
          </div>
          <div style={{
            fontSize: 12,
            color: currentStep >= 0 ? 'rgba(255,255,255,.6)' : '#999',
          }}>
            {order.status === 'pending_payment' && '请在30分钟内完成付款，逾期将自动取消'}
            {order.status === 'paid' && '卖家正在准备发货，请耐心等待'}
            {order.status === 'shipping' && `已发货，快递单号：${order.tracking_no || '待更新'}`}
            {order.status === 'delivered' && '商品已送达，请验收后确认收货'}
            {order.status === 'completed' && '交易已完成，感谢您的购买'}
            {order.status === 'cancelled' && '订单已取消'}
            {order.status === 'refunded' && '订单已退款'}
          </div>

          {/* Progress steps */}
          {currentStep >= 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', marginTop: 20, gap: 0,
            }}>
              {STEPS.map((s, i) => {
                const isActive = i <= currentStep;
                const isCurrent = i === currentStep;
                return (
                  <React.Fragment key={s.key}>
                    <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', margin: '0 auto 4px',
                        background: isActive ? '#6ECB8A' : 'rgba(255,255,255,.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: isActive ? '#fff' : 'rgba(255,255,255,.4)',
                        fontWeight: 600, border: isCurrent ? '2px solid #fff' : 'none',
                      }}>
                        {isActive ? '✓' : i + 1}
                      </div>
                      <div style={{
                        fontSize: 10, color: isActive ? '#fff' : 'rgba(255,255,255,.4)',
                      }}>{s.label}</div>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div style={{
                        flex: 1, height: 2, margin: '0 4px',
                        background: i < currentStep ? '#6ECB8A' : 'rgba(255,255,255,.15)',
                        marginBottom: 18,
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Product Card ── */}
        <div style={{
          margin: `0 ${isMobile ? 0 : px}px`, background: '#fff',
          border: `1px solid ${BD}`, borderTop: 'none',
          padding: 16,
        }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <img src={photo} alt="" style={{
              width: 80, height: 80, borderRadius: 6, objectFit: 'cover', flexShrink: 0,
              background: '#F5EDD8',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 600, color: INK, lineHeight: 1.4,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {order.item_name || '商品'}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {order.item_grade && (
                  <span style={{
                    padding: '1px 6px', borderRadius: 3, fontSize: 10,
                    background: '#F5EDD8', color: MUTED,
                  }}>{order.item_grade}级</span>
                )}
                {order.brand && (
                  <span style={{
                    padding: '1px 6px', borderRadius: 3, fontSize: 10,
                    background: '#F5EDD8', color: MUTED,
                  }}>{order.brand}</span>
                )}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: INK, marginTop: 6, fontFamily: 'var(--mono)' }}>
                ¥{priceCny.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* ── Order Info ── */}
        <div style={{
          margin: `16px ${isMobile ? 0 : px}px 0`, background: '#fff',
          border: `1px solid ${BD}`, borderRadius: isMobile ? 0 : 10, padding: 16,
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 12 }}>订单信息</div>
          {[
            ['订单号', order.order_no],
            ['下单时间', formatDate(order.created_at)],
            ['付款时间', order.paid_at ? formatDate(order.paid_at) : '—'],
            ['物流单号', order.tracking_no || '—'],
            ['完成时间', order.completed_at ? formatDate(order.completed_at) : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: 'flex', justifyContent: 'space-between', padding: '7px 0',
              borderBottom: `1px solid ${BD}30`, fontSize: 13,
            }}>
              <span style={{ color: MUTED }}>{k}</span>
              <span style={{ color: INK, fontFamily: k === '订单号' || k === '物流单号' ? 'var(--mono)' : 'inherit' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* ── Shipping Address ── */}
        {order.shipping_address && (
          <div style={{
            margin: `16px ${isMobile ? 0 : px}px 0`, background: '#fff',
            border: `1px solid ${BD}`, borderRadius: isMobile ? 0 : 10, padding: 16,
          }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 8 }}>收件地址</div>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
              {order.shipping_address}
            </div>
          </div>
        )}

        {/* ── Price Breakdown ── */}
        <div style={{
          margin: `16px ${isMobile ? 0 : px}px 0`, background: '#fff',
          border: `1px solid ${BD}`, borderRadius: isMobile ? 0 : 10, padding: 16,
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 12 }}>费用明细</div>
          {[
            ['商品金额', `¥${priceCny.toFixed(2)}`],
            ['平台佣金', `${Number(order.commission_pct || 8)}%`],
            ['卖家实收', `¥${Number(order.seller_revenue || 0).toFixed(2)}`],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: 'flex', justifyContent: 'space-between', padding: '7px 0',
              borderBottom: `1px solid ${BD}30`, fontSize: 13,
            }}>
              <span style={{ color: MUTED }}>{k}</span>
              <span style={{ color: INK }}>{v}</span>
            </div>
          ))}
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px',
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>实付金额</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: RED, fontFamily: 'var(--mono)' }}>
              ¥{priceCny.toFixed(2)}
            </span>
          </div>
        </div>

        {/* ── Seller Info ── */}
        <div style={{
          margin: `16px ${isMobile ? 0 : px}px 0`, background: '#fff',
          border: `1px solid ${BD}`, borderRadius: isMobile ? 0 : 10, padding: 16,
          marginBottom: 24,
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 12 }}>卖家信息</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', background: '#F5EDD8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 600, color: GOLD,
            }}>
              {(order.seller_name || '?').charAt(0)}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: INK }}>
                {order.seller_name ? order.seller_name.charAt(0) + '**' : '匿名卖家'}
              </div>
              <div style={{ fontSize: 12, color: MUTED }}>卖家</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Action Bar ── */}
      {(order.status === 'pending_payment' || order.status === 'paid' || order.status === 'delivered') && (
        <div style={{
          position: 'fixed', bottom: isMobile ? 56 : 0, left: 0, right: 0, background: '#fff',
          borderTop: `1px solid ${BD}`, padding: '10px 16px',
          display: 'flex', justifyContent: 'flex-end', gap: 10, zIndex: 101,
        }}>
          {order.status === 'pending_payment' && (
            <>
              <button onClick={() => handleAction('cancel')} disabled={actionLoading} style={{
                padding: '10px 24px', borderRadius: 20, border: `1px solid ${BD}`,
                background: '#fff', color: MUTED, fontSize: 14, cursor: 'pointer',
              }}>取消订单</button>
              <button onClick={() => handleAction('pay')} disabled={actionLoading} style={{
                padding: '10px 24px', borderRadius: 20, border: 'none',
                background: TEAL, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(26,107,84,.3)',
                opacity: actionLoading ? 0.6 : 1,
              }}>{actionLoading ? '处理中...' : '余额付款'}</button>
            </>
          )}
          {order.status === 'paid' && (
            <button onClick={() => setRefundModalOpen(true)} style={{
              padding: '10px 24px', borderRadius: 20, border: `1px solid ${BD}`,
              background: '#fff', color: RED, fontSize: 14, cursor: 'pointer',
            }}>申请退款</button>
          )}
          {order.status === 'delivered' && (
            <button onClick={() => setConfirmModalOpen(true)} style={{
              padding: '10px 32px', borderRadius: 20, border: 'none',
              background: TEAL, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(26,107,84,.3)',
            }}>确认收货</button>
          )}
        </div>
      )}

      {/* Refund Modal */}
      <Modal open={refundModalOpen} onCancel={() => setRefundModalOpen(false)} footer={null} title={null} width={360} centered>
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>💰</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: INK, marginBottom: 8 }}>申请退款</div>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: 16, lineHeight: 1.6 }}>
            退款金额：¥{Number(order.price_cny).toFixed(2)}，退回至账户余额
          </div>
          <textarea
            value={refundReason}
            onChange={e => setRefundReason(e.target.value)}
            placeholder="请填写退款原因（选填）"
            style={{
              width: '100%', minHeight: 80, padding: 10, borderRadius: 6,
              border: `1px solid ${BD}`, fontSize: 13, resize: 'vertical',
              marginBottom: 16, boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setRefundModalOpen(false)} style={{
              flex: 1, padding: '10px 0', borderRadius: 6, border: `1px solid ${BD}`,
              background: '#fff', color: MUTED, fontSize: 14, cursor: 'pointer',
            }}>取消</button>
            <button onClick={() => handleAction('refund')} disabled={actionLoading} style={{
              flex: 1, padding: '10px 0', borderRadius: 6, border: 'none',
              background: RED, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              opacity: actionLoading ? 0.6 : 1,
            }}>{actionLoading ? '提交中...' : '提交申请'}</button>
          </div>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <Modal open={confirmModalOpen} onCancel={() => setConfirmModalOpen(false)} footer={null} title={null} width={360} centered>
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: INK, marginBottom: 8 }}>确认收货？</div>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: 20, lineHeight: 1.6 }}>
            确认后款项将结算给卖家，请确保已收到商品并检查无误。
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setConfirmModalOpen(false)} style={{
              flex: 1, padding: '10px 0', borderRadius: 6, border: `1px solid ${BD}`,
              background: '#fff', color: MUTED, fontSize: 14, cursor: 'pointer',
            }}>取消</button>
            <button onClick={() => handleAction('confirm')} disabled={actionLoading} style={{
              flex: 1, padding: '10px 0', borderRadius: 6, border: 'none',
              background: TEAL, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              opacity: actionLoading ? 0.6 : 1,
            }}>{actionLoading ? '处理中...' : '确认收货'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
